<template>
  <div :id="chartId" style="height: 300px; width: 100%;" />
</template>

<script lang="ts">
import { PropType, defineComponent, markRaw } from 'vue';
import type * as echarts from 'echarts';

import { Nullable } from '@/utils/types';
import { IVisualizationDataItem } from '@/bundles/Visualizations/interfaces/IVisualizationDataItem';
import { loadEcharts } from '@/bundles/Visualizations/helpers/loadEcharts';
import { EchartsThemeMixin } from '@/bundles/Visualizations/mixins/EchartsThemeMixin';

interface IEChartPie {
  myChart: Nullable<echarts.ECharts>;
  isDestroyed: boolean;
}

export default defineComponent({
  name: 'EChartPie',

  mixins: [EchartsThemeMixin],

  props: {
    chartId: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    chartData: {
      type: Array as PropType<IVisualizationDataItem[]>,
      required: true
    },
    series: {
      type: Object as PropType<echarts.PieSeriesOption>,
      default: () => ({}),
    }
  },

  data: (): IEChartPie => ({
    myChart: null,
    isDestroyed: false,
  }),

  watch: {
    echartsTheme () {
      this.myChart?.dispose();
      this.myChart = null;
      this.loadChart();
    },
    chartData: {
      handler () {
        this.loadChart();
      },
      deep: true,
    }
  },

  mounted () { // can't be moved to watch because it will be called before mounted
    this.loadChart();
  },

  beforeUnmount () {
    this.isDestroyed = true;
    this.myChart?.dispose();
    this.myChart = null;
  },

  methods: {
    async loadChart () {
      if (this.isDestroyed) {
        return;
      }

      const container = document.getElementById(this.chartId);

      if (!container) {
        return;
      }

      const echarts = await loadEcharts();

      if (this.isDestroyed) {
        return;
      }

      if (!this.myChart) {
        // Kept out of Vue's reactivity: a proxied ECharts instance breaks its internal identity checks.
        this.myChart = markRaw(echarts.init(container, this.echartsTheme));
      }

      this.myChart.setOption({
          tooltip: {
            trigger: 'item'
          },
          legend: {
            top: 0,
            type: 'scroll',
            orient: 'vertical',
            left: 0,
            itemWidth: 20,
            itemHeight: 20,
            textStyle: {
              width: Math.round(this.myChart.getWidth() / 2) - 50, // 50 is the padding around pie
              overflow: 'break',
            }
          },
          series: [
            {
              name: this.label,
              type: 'pie',
              radius: ['40%', '70%'],
              padAngle: 0,
              itemStyle: {
                borderRadius: 0
              },
              data: this.chartData,
              emphasis: {
                label: {
                  show: true,
                  fontSize: 20,
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              label: {
                show: false,
                position: 'center'
              },
              center: ['70%', '50%'], // align to right
              color: [
                '#2563EB', // Primary Blue
                '#0D9488', // Teal
                '#EA580C', // Orange
                '#7C3AED', // Purple
                '#E11D48', // Rose
                '#0891B2', // Cyan
                '#D97706', // Amber
                '#4F46E5', // Indigo
                '#059669', // Emerald
                '#DB2777', // Pink
                '#475569', // Slate
                '#65A30D', // Lime
                '#0284C7', // Sky
                '#C026D3', // Fuchsia
                '#CA8A04', // Yellow
                '#DC2626', // Red
              ],
              ...this.series,
            }
          ]
        },
        true
      );
    }
  }
})
</script>
